import { useState, useEffect } from 'react';
import { HelpCircle, CheckCircle2, Eye, Trash2, Loader2 } from 'lucide-react';
import { ope } from '../../../services/api';
import { useToast } from '../../../context/ToastContext';
import ConfirmModal from '../../ConfirmModal';

const MAX_PREGUNTAS_POR_GENERACION = 100;
const MIN_PREGUNTAS_POR_GENERACION = 1;
const DEFAULT_PREGUNTAS_POR_GENERACION = 20;

const OpeAdminQuestionManager = ({ temaId, textoChars, onQuestionsUpdated }) => {
    const [promptPreguntas, setPromptPreguntas] = useState('');
    const [cantidadPreguntas, setCantidadPreguntas] = useState(DEFAULT_PREGUNTAS_POR_GENERACION);
    const [cantidadSugerida, setCantidadSugerida] = useState(DEFAULT_PREGUNTAS_POR_GENERACION);
    const [generatingPreguntas, setGeneratingPreguntas] = useState(false);
    const [preguntas, setPreguntas] = useState([]);
    const [preguntasExistentes, setPreguntasExistentes] = useState(0);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const { success, error, info } = useToast();

    useEffect(() => {
        if (!temaId) return;
        loadPromptPreguntas();
        loadPreguntas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temaId]);

    const loadPromptPreguntas = async () => {
        try {
            const data = await ope.adminGetPromptPreguntas(temaId);
            setPromptPreguntas(data.prompt);
            setCantidadSugerida(data.cantidadSugerida);
            const safeDefault = Math.min(MAX_PREGUNTAS_POR_GENERACION, Math.max(MIN_PREGUNTAS_POR_GENERACION, data.cantidadSugerida || DEFAULT_PREGUNTAS_POR_GENERACION));
            setCantidadPreguntas(safeDefault);
            setPreguntasExistentes(data.preguntasExistentes);
        } catch (err) {
            error(err.message || 'Error al cargar prompt');
        }
    };

    const loadPreguntas = async () => {
        try {
            const data = await ope.adminGetPreguntas(temaId);
            setPreguntas(data);
        } catch (err) {
            console.error('Error loading preguntas:', err);
        }
    };

    const handleGeneratePreguntas = async () => {
        try {
            setGeneratingPreguntas(true);
            const safeCantidad = Math.min(
                MAX_PREGUNTAS_POR_GENERACION,
                Math.max(MIN_PREGUNTAS_POR_GENERACION, Number.parseInt(cantidadPreguntas, 10) || MIN_PREGUNTAS_POR_GENERACION)
            );
            if (safeCantidad !== cantidadPreguntas) setCantidadPreguntas(safeCantidad);

            const result = await ope.adminGenerateQuestions(temaId, promptPreguntas, safeCantidad);
            const anadidas = result.anadidas ?? result['a\u00f1adidas'] ?? 0;

            success(`${anadidas} preguntas generadas (${result.duplicadas} duplicadas descartadas). Tokens: ${result.tokensUsados}`);
            if (result.warning) info(result.warning);

            await loadPreguntas();
            if (onQuestionsUpdated) onQuestionsUpdated();
        } catch (err) {
            error(`Error: ${err.message}`);
        } finally {
            setGeneratingPreguntas(false);
        }
    };

    const confirmPublicarTodas = async () => {
        try {
            const result = await ope.adminPublicarTodas(temaId);
            success(`${result.actualizadas} preguntas publicadas`);
            setConfirmModalOpen(false);
            await loadPreguntas();
            if (onQuestionsUpdated) onQuestionsUpdated();
        } catch {
            error('Error al publicar preguntas');
            setConfirmModalOpen(false);
        }
    };

    const handleDeletePregunta = async (id) => {
        try {
            await ope.adminDeletePregunta(id);
            setPreguntas(prev => prev.filter(p => p.id !== id));
            success('Pregunta eliminada');
            if (onQuestionsUpdated) onQuestionsUpdated();
        } catch {
            error('Error al eliminar');
        }
    };

    const handleToggleEstado = async (pregunta) => {
        const nuevoEstado = pregunta.estado === 'publicada' ? 'borrador' : 'publicada';
        try {
            await ope.adminCambiarEstado(pregunta.id, nuevoEstado);
            setPreguntas(prev => prev.map(p =>
                p.id === pregunta.id ? { ...p, estado: nuevoEstado } : p
            ));
            if (onQuestionsUpdated) onQuestionsUpdated();
        } catch {
            error('Error al cambiar estado');
        }
    };

    return (
        <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-4 ml-4">
            {/* Info de cálculo */}
            <div className="p-3 bg-indigo-600/10 border border-indigo-500/30 rounded-lg text-sm">
                <p className="text-indigo-300">
                    📊 Texto: <strong>{(textoChars || 0).toLocaleString()}</strong> caracteres →
                    Se recomiendan <strong>~{cantidadSugerida}</strong> preguntas.
                </p>
                {preguntasExistentes > 0 && (
                    <p className="text-yellow-400 mt-1">
                        ⚠️ Ya existen {preguntasExistentes} preguntas para este tema (duplicados se descartarán)
                    </p>
                )}
            </div>

            {/* Cantidad editable */}
            <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Nº de preguntas:</label>
                <input
                    type="number"
                    min={MIN_PREGUNTAS_POR_GENERACION}
                    max={MAX_PREGUNTAS_POR_GENERACION}
                    value={cantidadPreguntas}
                    onChange={e => setCantidadPreguntas(parseInt(e.target.value, 10) || MIN_PREGUNTAS_POR_GENERACION)}
                    className="w-24 px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-center focus:outline-none focus:border-indigo-500"
                />
                <button
                    onClick={() => setCantidadPreguntas(Math.min(MAX_PREGUNTAS_POR_GENERACION, Math.max(MIN_PREGUNTAS_POR_GENERACION, cantidadSugerida || DEFAULT_PREGUNTAS_POR_GENERACION)))}
                    className="text-xs text-indigo-400 hover:text-indigo-300 underline"
                >
                    Usar sugerida ({cantidadSugerida})
                </button>
            </div>

            {/* Prompt editable */}
            <div>
                <label className="block text-sm text-gray-400 mb-2">
                    Prompt para GPT-4o <span className="text-indigo-400">(editable)</span>:
                </label>
                <textarea
                    value={promptPreguntas}
                    onChange={e => setPromptPreguntas(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-indigo-500 resize-y"
                />
                <p className="text-xs text-gray-500 mt-1">
                    Placeholders: <code className="text-indigo-400">{'{texto}'}</code> y <code className="text-indigo-400">{'{cantidad}'}</code>
                </p>
            </div>

            <div className="flex gap-2">
                <button
                    onClick={handleGeneratePreguntas}
                    disabled={generatingPreguntas || !promptPreguntas}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                    {generatingPreguntas ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Generando preguntas...</>
                    ) : (
                        <><HelpCircle className="w-4 h-4" /> Generar {cantidadPreguntas} Preguntas</>
                    )}
                </button>

                {preguntas.length > 0 && preguntas.some(p => p.estado !== 'publicada') && (
                    <button
                        onClick={() => setConfirmModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                    >
                        <CheckCircle2 className="w-4 h-4" /> Publicar Todas
                    </button>
                )}
            </div>

            {/* Lista de preguntas */}
            {preguntas.length > 0 && (
                <div className="space-y-2 mt-4">
                    <h4 className="text-sm font-medium text-gray-400">
                        Preguntas ({preguntas.length}):
                    </h4>
                    <div className="max-h-96 overflow-y-auto space-y-2">
                        {preguntas.map((p, i) => (
                            <div key={p.id} className="p-3 bg-black/20 rounded-lg border border-white/5">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                        <p className="text-sm text-white">
                                            <span className="text-gray-500">{i + 1}.</span> {p.pregunta}
                                        </p>
                                        <div className="flex gap-2 mt-1">
                                            <span className={`text-xs px-2 py-0.5 rounded-full
                                                ${p.estado === 'publicada' ? 'bg-green-600/20 text-green-400' :
                                                    p.estado === 'auto_validada' ? 'bg-blue-600/20 text-blue-400' :
                                                        'bg-gray-600/20 text-gray-400'}`}>
                                                {p.estado}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {p.dificultad} | ✓ {p.respuesta_correcta}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleToggleEstado(p)}
                                            className={`p-1.5 rounded-lg transition-colors ${p.estado === 'publicada'
                                                ? 'text-green-400 hover:bg-green-600/20'
                                                : 'text-gray-500 hover:bg-white/10'}`}
                                            title={p.estado === 'publicada' ? 'Despublicar' : 'Publicar'}
                                        >
                                            {p.estado === 'publicada' ? <CheckCircle2 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => handleDeletePregunta(p.id)}
                                            className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-600/10 rounded-lg transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={confirmPublicarTodas}
                title="Publicar preguntas"
                message="¿Publicar todas las preguntas en borrador?"
                confirmText="Publicar"
                cancelText="Cancelar"
                variant="warning"
            />
        </div>
    );
};

export default OpeAdminQuestionManager;
